import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { MapPin, Clock, Zap, ShieldCheck, Star, Upload, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { cn, tr, STATUS_COLORS, fmtDate } from "@/lib/utils";
import { api, fileUrl, errMsg } from "@/lib/api";
import { toast } from "sonner";

export const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  return <span data-testid={`status-${status}`} className={cn("z-chip", STATUS_COLORS[status] || "bg-slate-100 text-slate-700")}>{t(`status.${status}`, status)}</span>;
};

export const RequestCard = ({ r, to, action }) => {
  const { t } = useTranslation();
  const inner = (
    <div className="z-card p-5 transition-transform duration-200 hover:-translate-y-0.5" data-testid={`request-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-extrabold text-[#0B192C]">{tr(r, "service_name")}</h3>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><MapPin size={13} />{tr(r, "area_name")}، {tr(r, "city_name")}</span>
            <span className="inline-flex items-center gap-1"><Clock size={13} />{t(`time.${r.time_option}`)}</span>
            {r.priority === "urgent" && <span className="inline-flex items-center gap-1 text-amber-600"><Zap size={13} />{t("req.urgent")}</span>}
            {r.distance_km != null && <span className="font-bold text-[#06B6D4]">~{r.distance_km} {t("common.km")}</span>}
          </p>
        </div>
        <StatusBadge status={r.status} />
      </div>
      <p className="mt-3 line-clamp-2 text-sm text-foreground/80">{r.description}</p>
      {r.images?.length > 0 && <div className="mt-3 flex gap-2">{r.images.slice(0, 4).map((u) => <img key={u} src={fileUrl(u)} alt="" loading="lazy" className="h-14 w-14 rounded-lg object-cover" />)}</div>}
      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground"><span>{fmtDate(r.created_at)}</span>{action}</div>
    </div>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
};

let _svcNames = null;
const useServiceNames = () => {
  const [m, setM] = useState(_svcNames);
  useEffect(() => {
    if (_svcNames) return;
    api.get("/catalog/services").then((r) => { _svcNames = Object.fromEntries(r.data.map((s) => [s.id, s])); setM(_svcNames); }).catch(() => {});
  }, []);
  return m || {};
};

export const PartnerCard = ({ p }) => {
  const { t } = useTranslation();
  const names = useServiceNames();
  const specialties = (p.service_ids || []).map((id) => names[id]).filter(Boolean).slice(0, 2);
  return (
    <Link to={`/partners/${p.id}`} data-testid={`partner-card-${p.id}`} className="z-card group flex flex-col p-5 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_20px_40px_-18px_rgba(11,25,44,0.28)]">
      <div className="flex items-start gap-4">
        <div className="relative shrink-0">
          <img src={fileUrl(p.avatar_url) || `https://api.dicebear.com/9.x/initials/svg?seed=${encodeURIComponent(p.name)}&backgroundColor=0B192C`} alt={p.name} className="h-[68px] w-[68px] rounded-2xl object-cover ring-2 ring-white shadow-md" loading="lazy" />
          {p.is_available && <span title={t("partner.available")} className="absolute -bottom-1 -end-1 h-4 w-4 rounded-full border-2 border-white bg-[#10B981]" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="truncate text-base font-extrabold text-[#0B192C]">{p.name}</h3>
            {p.is_verified && <span className="z-chip bg-[#10B981]/10 text-[#059669]" data-testid="partner-verified-chip"><ShieldCheck size={12} />{p.partner_type === "company" ? t("verify.badge_company") : t("verify.badge_individual")}</span>}
          </div>
          {(p.profession_title || specialties.length > 0) && <p className="mt-0.5 truncate text-xs font-semibold text-[#1E3A8A]" data-testid="partner-specialty">{p.profession_title || specialties.map((s) => tr(s)).join(" · ")}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1 font-bold text-amber-500"><Star size={13} fill="currentColor" />{p.rating_avg || "—"}<span className="font-normal text-muted-foreground">({p.rating_count})</span></span>
            <span>{p.experience_years} {t("search.years")}</span>
            <span>{p.completed_count} {t("search.done")}</span>
          </div>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3 border-t border-border/70 pt-3">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {p.is_available ? <span className="z-chip bg-[#06B6D4]/10 text-[#0e7490]"><span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" />{t("partner.available")}</span> : <span className="z-chip bg-muted text-muted-foreground">{t("partner.unavailable")}</span>}
          {p.distance_km != null && <span className="inline-flex items-center gap-1 font-bold text-[#06B6D4]"><MapPin size={12} />~{p.distance_km} {t("common.km")}</span>}
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-[#0B192C] px-3.5 py-1.5 text-xs font-bold text-white transition-colors group-hover:bg-[#1E3A8A]" data-testid="partner-view-profile">{t("flow.view_profile")}</span>
      </div>
    </Link>
  );
};

async function compressImage(f) {
  if (!f.type.startsWith("image/") || f.type === "image/heic" || f.type === "image/heif" || f.size < 900 * 1024) return f;
  try {
    const bmp = await createImageBitmap(f);
    const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas"); c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.85));
    return blob ? new File([blob], (f.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }) : f;
  } catch { return f; }
}

export const Uploader = ({ onUploaded, kind = "image", label, accept = "image/*", testId = "file-upload", camera = false, multiple = false, max = 1 }) => {
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [menu, setMenu] = useState(false);
  const up = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, max); if (!files.length) return;
    setBusy(true); setMenu(false);
    for (const raw of files) {
      if (raw.size > 15 * 1024 * 1024) { toast.error("حجم الملف كبير (الحد 15MB) / File too large (max 15MB)"); continue; }
      setPct(0);
      const f = kind === "document" && raw.type === "application/pdf" ? raw : await compressImage(raw);
      const fd = new FormData(); fd.append("file", f);
      try { const { data } = await api.post(`/uploads?kind=${kind}`, fd, { onUploadProgress: (ev) => ev.total && setPct(Math.round((ev.loaded / ev.total) * 100)) }); onUploaded(data.url); } catch (err) { toast.error(errMsg(err)); }
    }
    setBusy(false); e.target.value = "";
  };
  const inputCls = "hidden";
  const btnCls = "inline-flex cursor-pointer items-center gap-2 rounded-full border-2 border-dashed border-[#06B6D4]/40 px-4 py-2.5 text-sm font-bold text-[#0B192C] hover:bg-[#06B6D4]/5";
  if (!camera) return (
    <label className={btnCls}>
      {busy ? <><Loader2 size={16} className="animate-spin" /><span data-testid={`${testId}-progress`}>{pct}%</span></> : <><Upload size={16} />{label}</>}<input data-testid={testId} type="file" accept={accept} multiple={multiple} className={inputCls} onChange={up} disabled={busy} />
    </label>
  );
  return (
    <div className="relative">
      <button type="button" data-testid={testId} disabled={busy} onClick={() => setMenu((m) => !m)} className={btnCls}>{busy ? <><Loader2 size={16} className="animate-spin" /><span data-testid={`${testId}-progress`}>{pct}%</span></> : <><Upload size={16} />{label}</>}</button>
      {menu && <div className="absolute z-20 mt-2 w-56 rounded-2xl border bg-white p-2 shadow-xl" data-testid={`${testId}-menu`}>
        <label className="flex cursor-pointer items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold hover:bg-muted">📷 {i18n.language === "en" ? "Take a photo" : "التقاط صورة"}<input data-testid={`${testId}-camera`} type="file" accept="image/*" capture="environment" className={inputCls} onChange={up} /></label>
        <label className="flex cursor-pointer items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold hover:bg-muted">🖼️ {i18n.language === "en" ? "Choose from gallery" : "اختيار من الاستديو"}<input data-testid={`${testId}-gallery`} type="file" accept="image/*,.heic,.heif" multiple={multiple} className={inputCls} onChange={up} /></label>
      </div>}
    </div>
  );
};

export const Stat = ({ label, value, icon: Icon, tone = "navy", testId }) => (
  <div data-testid={testId} className={cn("rounded-2xl p-5", tone === "navy" ? "z-card-dark" : "z-card")}>
    <div className="flex items-center justify-between"><span className={cn("text-xs font-semibold", tone === "navy" ? "text-white/60" : "text-muted-foreground")}>{label}</span>{Icon && <Icon size={18} className="text-[#06B6D4]" />}</div>
    <div className="mt-2 text-2xl font-extrabold">{value}</div>
  </div>
);
